// Runtime helpers that decide whether a given original media URL has an
// optimized sibling (a `<name>.webp`, `<name>-480.webp`, `<name>-960.webp`,
// `<name>.web.mp4`, or `<name>.poster.jpg`) sitting next to it in
// `public/img/`.
//
// Why a build-time manifest instead of just rewriting product URLs:
//   - productData.js stays a single source of truth (originals).
//   - If an optimized sibling is missing (the optimize script hasn't been
//     re-run since a new image was added, an encoder failure, etc.) the
//     helpers return `undefined` and the consuming `<picture>` falls back
//     to the original `<img>` — the page never breaks.
//   - No filesystem access at runtime: the manifest is a plain JSON object
//     bundled with the app, so lookups are an O(1) hash check.
//
// Regenerate the manifest with `node scripts/build-image-manifest.mjs`
// (the `scripts/optimize-images.sh` wrapper invokes this automatically).

import manifest from "../data/optimizedImageManifest.json";

const RASTER_EXT_RE = /\.(png|jpe?g)$/i;
const VIDEO_EXT_RE = /\.(mp4|mov)$/i;

const hasEntry = (url) => Boolean(url) && manifest[url] === true;

/**
 * Resolve the URL of a `.webp` sibling for a raster image.
 *
 * @param {string | undefined | null} src    e.g. `/img/foo/bar.png`
 * @param {number | undefined} [width]       `480`, `960`, or omit for full
 * @returns {string | undefined}             the webp URL if one exists in
 *                                           the manifest, else `undefined`
 *                                           (caller should fall back to the
 *                                           original `<img src>`).
 */
export const webpVariant = (src, width) => {
  if (typeof src !== "string" || src.length === 0) return undefined;
  if (!RASTER_EXT_RE.test(src)) return undefined;

  const base = src.replace(RASTER_EXT_RE, "");
  const suffix =
    width === 480 ? "-480.webp" : width === 960 ? "-960.webp" : ".webp";
  const candidate = `${base}${suffix}`;
  return hasEntry(candidate) ? candidate : undefined;
};

/**
 * Resolve the URL of the small `.poster.jpg` generated alongside a video,
 * suitable for the `<video poster>` attribute.
 *
 * Currently exported for future consumers; no component wires it in yet.
 *
 * @param {string | undefined | null} src    e.g. `/img/foo/clip.mp4`
 * @returns {string | undefined}             the poster URL if one exists,
 *                                           else `undefined`.
 */
export const videoPoster = (src) => {
  if (typeof src !== "string" || src.length === 0) return undefined;
  if (!VIDEO_EXT_RE.test(src)) return undefined;

  const candidate = src.replace(VIDEO_EXT_RE, ".poster.jpg");
  return hasEntry(candidate) ? candidate : undefined;
};

/**
 * Resolve the URL of the optimized `.web.mp4` sibling (720p H.264 +
 * faststart, no audio) for a given video source.
 *
 * Currently exported for future consumers; no component wires it in yet.
 *
 * @param {string | undefined | null} src    e.g. `/img/foo/clip.mp4`
 * @returns {string | undefined}             the optimized video URL if one
 *                                           exists, else `undefined`.
 */
export const videoOptimized = (src) => {
  if (typeof src !== "string" || src.length === 0) return undefined;
  if (!VIDEO_EXT_RE.test(src)) return undefined;

  const candidate = src.replace(VIDEO_EXT_RE, ".web.mp4");
  return hasEntry(candidate) ? candidate : undefined;
};
